import type { ApiClient } from "../api-client";
import type {
  CheckinUploadUrlResponse,
  CreateCheckinRequest,
  CreateCheckinResponse,
  LandmarkCheckin,
  LandmarkCheckinsResponse,
  LandmarkIndexResponse,
  NearbyLandmarksResponse,
} from "../types/landmarks";
import type { BulkCheckinMatchResponse, BulkPhotoInput } from "../types/bulk-checkins";

// Photo check-in + landmark discovery fetchers (spec 08 LMK-10), shared by
// web and mobile the same way as api/social.ts.

export function getCheckinUploadUrl(
  client: ApiClient,
  contentType: string,
): Promise<CheckinUploadUrlResponse> {
  return client.post("/landmarks/checkins/upload-url", { contentType });
}

export function createLandmarkCheckin(
  client: ApiClient,
  landmarkSlug: string,
  body: CreateCheckinRequest,
): Promise<CreateCheckinResponse> {
  return client.post(`/landmarks/${encodeURIComponent(landmarkSlug)}/checkins`, body);
}

export function getLandmarkCheckins(
  client: ApiClient,
  landmarkSlug: string,
  cursor?: string,
  opts?: { mine?: boolean; random?: boolean },
): Promise<LandmarkCheckinsResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (opts?.mine) params.set("mine", "1");
  if (opts?.random) params.set("random", "1");
  const qs = params.toString();
  const path = `/landmarks/${encodeURIComponent(landmarkSlug)}/checkins`;
  return client.get(qs ? `${path}?${qs}` : path);
}

/** Summit photo check-in (spec 34). Refused with 422 + SummitCheckinRefusalCode. */
export function createPeakCheckin(
  client: ApiClient,
  peakSlug: string,
  body: CreateCheckinRequest,
): Promise<CreateCheckinResponse> {
  return client.post(`/peaks/${encodeURIComponent(peakSlug)}/checkins`, body);
}

/** A peak's gallery: summit check-ins plus trig pillar check-ins that collected it. */
export function getPeakCheckins(
  client: ApiClient,
  peakSlug: string,
  cursor?: string,
  opts?: { mine?: boolean; random?: boolean },
): Promise<LandmarkCheckinsResponse> {
  const params = new URLSearchParams();
  if (cursor) params.set("cursor", cursor);
  if (opts?.mine) params.set("mine", "1");
  if (opts?.random) params.set("random", "1");
  const qs = params.toString();
  const path = `/peaks/${encodeURIComponent(peakSlug)}/checkins`;
  return client.get(qs ? `${path}?${qs}` : path);
}

export function updateCheckin(
  client: ApiClient,
  id: string,
  updates: { isPrivate?: boolean; caption?: string | null },
): Promise<{ checkin: LandmarkCheckin }> {
  return client.patch(`/landmarks/checkins/${id}`, updates);
}

export function deleteCheckin(client: ApiClient, id: string): Promise<{ ok: boolean }> {
  return client.delete(`/landmarks/checkins/${id}`);
}

export function reportCheckin(
  client: ApiClient,
  id: string,
  reason?: string,
): Promise<{ ok: boolean }> {
  return client.post(`/landmarks/checkins/${id}/report`, { reason });
}

export function getNearbyLandmarks(
  client: ApiClient,
  lat: number,
  lon: number,
  radiusM?: number,
): Promise<NearbyLandmarksResponse> {
  const params = new URLSearchParams({ lat: String(lat), lon: String(lon) });
  if (radiusM !== undefined) params.set("radiusM", String(radiusM));
  return client.get(`/landmarks/nearby?${params}`);
}

export function getLandmarkIndex(client: ApiClient): Promise<LandmarkIndexResponse> {
  return client.get("/landmarks/index");
}

/** Bulk photo check-ins (spec 39): what each located photo was taken at. Sends
 * coordinates and capture times only, never the photos. */
export function matchCheckinPhotos(
  client: ApiClient,
  photos: BulkPhotoInput[],
): Promise<BulkCheckinMatchResponse> {
  return client.post("/landmarks/checkins/match", { photos });
}

/** Run stats, achievements, events and goals once after a bulk batch. */
export function finishCheckinBatch(client: ApiClient, batchId: string): Promise<{ ok: boolean }> {
  return client.post("/landmarks/checkins/finish", { batchId });
}
